// The rail's head, *Acme ▾*, and the workspace menu it opens
// (02-DESIGN/03-console/01-the-console.md § the shape: the workspace menu;
// hq decision 0069 point 5): drawn in the rail under its head, as the
// person's menu is drawn over its foot, and closed on a pick, on Escape and
// on a click elsewhere. The memberships are read when the menu first opens
// in a session, on a connection to the identity plane that lasts for the
// read; the children are the list *Workspaces* reads, for an admin of a
// workspace that manages workspaces. A pick goes to that workspace in the
// same tab, with no page asking first.

import { Check, ChevronDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import type { Who } from "../platform/bus.ts";
import { readChildren } from "../platform/moves.ts";
import { useConnection } from "../platform/session.tsx";
import { pageOffered } from "../roles/offers.ts";
import { useMoves } from "../surfaces/workspace/moves.tsx";
import type { Child } from "../wire/auth.ts";
import type { Membership } from "../wire/identity.ts";
import { leaveThen } from "./fold.ts";
import { Mark, type Live } from "./Mark.tsx";
import { FOOT_WORDS, workspaceMenu, type MenuFoot, type MenuRow } from "./workspace-menu.ts";

/** Read is one of the menu's reads: not asked yet, under way, answered, or refused. */
type Read<T> = { state: "idle" } | { state: "pending" } | { state: "ready"; value: T } | { state: "refused" };

export function WorkspaceMenu({
  slug,
  name,
  role,
  live,
  onLeave,
}: {
  slug: string;
  name: string;
  role: Who["role"];
  live: Live;
  /** What a move out of here does first: at a phone's width, the open rail closes (./fold.ts leaveThen). */
  onLeave?: () => void;
}) {
  const moves = useMoves();
  const { bus } = useConnection();
  const [open, setOpen] = useState(false);
  const [memberships, setMemberships] = useState<Read<Membership[]>>({ state: "idle" });
  const [children, setChildren] = useState<Read<Child[]>>({ state: "idle" });
  const head = useRef<HTMLDivElement>(null);
  // The plane connection a memberships read holds open: given up when the
  // menu goes away or a pick is made mid-read.
  const reading = useRef<AbortController | undefined>(undefined);
  useEffect(
    () => () => {
      reading.current?.abort();
    },
    [],
  );
  const admin = pageOffered(role, "workspaces");

  useEffect(() => {
    if (!open) {
      return;
    }
    const onDown = (e: MouseEvent) => {
      if (head.current !== null && !head.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (moves === undefined) {
    return (
      <div className="workspace">
        <Mark live={live} />
        <span className="workspace-name">{name}</span>
      </div>
    );
  }

  const shape = workspaceMenu({
    current: role === undefined ? { slug, name } : { slug, name, role },
    onPlatform: moves.onPlatform,
    signUp: moves.signUp,
    ...(memberships.state === "ready" ? { memberships: memberships.value } : {}),
    ...(children.state === "ready" ? { children: children.value } : {}),
  });
  // The reads start when the menu first opens, never at the rail's first draw.
  const read = () => {
    if (moves.onPlatform && memberships.state === "idle") {
      setMemberships({ state: "pending" });
      const ctl = new AbortController();
      reading.current = ctl;
      moves.memberships(ctl.signal).then(
        (value) => {
          setMemberships({ state: "ready", value });
        },
        (err: unknown) => {
          if (ctl.signal.aborted) {
            // Read again the next time the menu opens.
            setMemberships({ state: "idle" });
            return;
          }
          console.error("Could not read your workspaces", err);
          setMemberships({ state: "refused" });
        },
      );
    }
    if (admin && children.state === "idle") {
      setChildren({ state: "pending" });
      // A workspace that may not manage workspaces refuses the list: it lists no child.
      readChildren(bus).then(
        (value) => {
          setChildren({ state: "ready", value });
        },
        () => {
          setChildren({ state: "refused" });
        },
      );
    }
  };
  const pick = (to: string, member: boolean | undefined) => {
    setOpen(false);
    reading.current?.abort();
    leaveThen(onLeave, () => {
      moves.enter(to, member);
    });
  };
  const foot = (f: MenuFoot) => {
    setOpen(false);
    reading.current?.abort();
    leaveThen(onLeave, () => {
      if (f === "all") {
        moves.all();
      } else if (f === "create") {
        moves.create();
      } else {
        moves.elsewhere();
      }
    });
  };
  return (
    <div className="rail-head" ref={head}>
      <button
        type="button"
        className="workspace switch"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`${name} — workspaces`}
        onClick={() => {
          if (!open) {
            read();
          }
          setOpen(!open);
        }}
      >
        <Mark live={live} />
        <span className="workspace-name">{name}</span>
        <ChevronDown className="chev" strokeWidth={1.6} aria-hidden="true" />
      </button>
      {open && (
        <div className="menu down" role="menu" aria-label="Workspaces">
          {shape.memberships.map((r) => (
            <Row
              key={r.slug}
              row={r}
              onPick={() => {
                // A membership's row is a workspace the sign-in admits.
                pick(r.slug, moves.onPlatform && memberships.state === "ready" ? true : undefined);
              }}
            />
          ))}
          {memberships.state === "pending" && <p className="menu-note">Reading your workspaces…</p>}
          {memberships.state === "refused" && (
            <p className="menu-note">Your other workspaces could not be read.</p>
          )}
          {shape.managed !== undefined && (
            <>
              <div className="menu-group">{shape.managed.heading}</div>
              {shape.managed.rows.map((r) => (
                <Row
                  key={r.slug}
                  row={r}
                  onPick={() => {
                    // A child's row: whether the sign-in admits it is decided on the pick.
                    pick(r.slug, undefined);
                  }}
                />
              ))}
            </>
          )}
          {shape.foot.length > 0 && <hr />}
          {shape.foot.map((f) => (
            <button
              key={f}
              type="button"
              role="menuitem"
              onClick={() => {
                foot(f);
              }}
            >
              {FOOT_WORDS[f]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Row is one workspace of the menu: its name, the role where it is a membership, the current one marked. */
function Row({ row: r, onPick }: { row: MenuRow; onPick: () => void }) {
  return (
    <button
      type="button"
      role="menuitem"
      className="menu-row"
      aria-current={r.current ? "true" : undefined}
      onClick={onPick}
    >
      <span className="menu-row-text">
        <span className="menu-row-name">{r.name}</span>
        {r.role !== undefined && <span className="menu-row-sub">{r.role}</span>}
      </span>
      {r.current && <Check size={14} strokeWidth={1.8} aria-label="current" />}
    </button>
  );
}
